import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Wallet, ArrowUpRight, ArrowDownRight, Activity,
  TrendingUp, Send, Filter
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const TX_TYPE_META = {
  earn_attendance: { label: 'Attendance Reward', color: 'lime' },
  earn_streak: { label: 'Streak Bonus', color: 'lime' },
  earn_referral: { label: 'Referral Reward', color: 'lime' },
  earn_off_peak: { label: 'Off-Peak Bonus', color: 'lime' },
  stake_lock: { label: 'Stake Locked', color: 'blue' },
  stake_release: { label: 'Stake Released', color: 'blue' },
  stake_forfeit: { label: 'Stake Forfeited', color: 'pink' },
  spend_discount: { label: 'Discount Used', color: 'pink' },
  spend_boost: { label: 'Visibility Boost', color: 'pink' },
};

export default function WalletPage() {
  const { api } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [wRes, tRes] = await Promise.all([
        api.get('/wallet'),
        api.get('/wallet/transactions?limit=50'),
      ]);
      setWallet(wRes.data);
      setTransactions(tRes.data);
    } catch (err) {
      console.error('Wallet fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = filter === 'all' ? transactions :
    filter === 'earned' ? transactions.filter(t => t.amount > 0) :
    transactions.filter(t => t.amount < 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="wallet-page" className="space-y-6">
      {/* Header */}
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight">Wallet</h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">Your $PLAY token balance and history</p>
      </motion.div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div {...fadeUp} transition={{ delay: 0.1 }}
          className="col-span-2 bg-gradient-to-br from-blue-600/20 to-blue-600/5 border border-blue-500/20 rounded-xl p-5 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-[60px]" />
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={18} className="text-blue-400" />
            <span className="text-xs text-blue-400 uppercase tracking-wider font-manrope">Available Balance</span>
          </div>
          <p className="font-barlow text-4xl font-bold text-white">{wallet?.balance || 0}</p>
          <p className="text-blue-300/60 text-sm mt-1 font-manrope">$PLAY Tokens</p>
        </motion.div>

        <motion.div {...fadeUp} transition={{ delay: 0.15 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-lime-400/20 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Send size={14} className="text-lime-400" />
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Staked</span>
          </div>
          <p className="font-barlow text-2xl font-bold text-white">{wallet?.staked || 0}</p>
        </motion.div>

        <motion.div {...fadeUp} transition={{ delay: 0.2 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-slate-400" />
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Lifetime</span>
          </div>
          <p className="font-barlow text-2xl font-bold text-white">{wallet?.lifetime_earned || 0}</p>
        </motion.div>
      </div>

      {/* Transactions */}
      <motion.div {...fadeUp} transition={{ delay: 0.25 }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <Activity size={16} className="text-blue-400" />
            Transaction History
          </h3>
          <div className="flex items-center gap-1.5 bg-slate-900/50 rounded-full p-0.5 border border-white/5">
            {['all', 'earned', 'spent'].map(f => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs transition-all ${
                  filter === f ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden" data-testid="wallet-transactions">
          {filtered.map((tx, i) => {
            const meta = TX_TYPE_META[tx.type] || { label: tx.type, color: 'slate' };
            return (
              <div key={tx.id || i} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  tx.amount > 0 ? 'bg-lime-500/20' : 'bg-pink-500/20'
                }`}>
                  {tx.amount > 0 ? <ArrowUpRight size={16} className="text-lime-400" /> : <ArrowDownRight size={16} className="text-pink-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{meta.label}</p>
                  <p className="text-slate-500 text-xs truncate">{tx.description}</p>
                </div>
                <div className="text-right">
                  <span className={`font-barlow font-bold text-sm ${tx.amount > 0 ? 'text-lime-400' : 'text-pink-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount} $PLAY
                  </span>
                  <p className="text-slate-600 text-[10px] font-mono">{new Date(tx.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">No transactions found</div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
